/**
 * Turns a stored session-event payload (KIR-4445) into labelled fields for
 * the expanded timeline row.
 */

export type DetailValue =
  | { kind: 'text'; text: string }
  | { kind: 'code'; text: string };

export interface DetailField {
  label: string;
  value: DetailValue;
}

type Obj = Record<string, unknown>;

function asObj(v: unknown): Obj | null {
  return v !== null && typeof v === 'object' && !Array.isArray(v)
    ? (v as Obj)
    : null;
}

function asText(v: unknown): string | null {
  return typeof v === 'string' && v.trim() !== '' ? v : null;
}

function parseJson(text: string): unknown {
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return undefined;
  }
}

/** "file_path" / "filePath" → "File path". */
export function humanizeKey(key: string): string {
  const words = key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * Values read from a JSON number or boolean. Copies of shown content are
 * always strings, so these are never dropped as repeats: an edit's
 * `replace_all: true` must show even when another field also reads "true".
 */
const scalarValues = new WeakSet<DetailValue>();

function scalarOrCode(v: unknown): DetailValue | null {
  if (v === null || v === undefined || v === '') return null;
  if (typeof v === 'string') return { kind: 'text', text: v };
  if (typeof v === 'number' || typeof v === 'boolean') {
    const value: DetailValue = { kind: 'text', text: String(v) };
    scalarValues.add(value);
    return value;
  }
  return { kind: 'code', text: JSON.stringify(v, null, 2) };
}

/**
 * A label read from a payload key. Payload keys come from the tool call, so
 * they always carry their source ("Input › Model") and can never pass for the
 * row's own labels such as Model or Permission mode.
 */
function keyLabel(source: string, key: string): string {
  return `${source} › ${humanizeKey(key)}`;
}

/** One field per key of an object, labelled from the key under source. */
function objectFields(obj: Obj, source: string): DetailField[] {
  const out: DetailField[] = [];
  for (const [key, v] of Object.entries(obj)) {
    const value = scalarOrCode(v);
    if (value) out.push({ label: keyLabel(source, key), value });
  }
  return out;
}

/** A tool's JSON-string input or result: its keys when it is an object. */
function jsonTextFields(text: string, label: string): DetailField[] {
  const parsed = parseJson(text);
  const obj = asObj(parsed);
  if (obj) return objectFields(obj, label);
  if (parsed !== undefined && typeof parsed !== 'string') {
    return [
      { label, value: { kind: 'code', text: JSON.stringify(parsed, null, 2) } },
    ];
  }
  return [
    {
      label,
      value: { kind: 'text', text: typeof parsed === 'string' ? parsed : text },
    },
  ];
}

function text(label: string, v: unknown): DetailField[] {
  const t = asText(v);
  return t ? [{ label, value: { kind: 'text', text: t } }] : [];
}

function code(label: string, v: unknown): DetailField[] {
  const t = asText(v);
  return t ? [{ label, value: { kind: 'code', text: t } }] : [];
}

function duration(v: unknown): DetailField[] {
  return typeof v === 'number' && v > 0
    ? [{ label: 'Duration', value: { kind: 'text', text: `${v} ms` } }]
    : [];
}

/**
 * An edit's tool input. A MultiEdit's `edits` array repeats the replaced and
 * new text the row already shows, so only each item's other keys (such as
 * `replace_all`) are listed, numbered like the edits.
 */
function editInputFields(input: Obj, edit: Obj): DetailField[] {
  const editStrings = new Set(
    (Array.isArray(edit.edits) ? edit.edits : [])
      .flatMap(e => [asObj(e)?.oldString, asObj(e)?.newString])
      .filter((s): s is string => typeof s === 'string')
  );
  const fields: DetailField[] = [];
  for (const [key, v] of Object.entries(input)) {
    const items = Array.isArray(v) ? v.map(asObj) : null;
    if (!items || items.length === 0 || items.some(item => item === null)) {
      fields.push(...objectFields({ [key]: v }, 'Input'));
      continue;
    }
    (items as Obj[]).forEach((item, i) => {
      const n = items.length > 1 ? ` ${i + 1}` : '';
      for (const [k, s] of Object.entries(item)) {
        if (typeof s === 'string' && editStrings.has(s)) continue;
        const value = scalarOrCode(s);
        if (value) fields.push({ label: `${keyLabel('Input', k)}${n}`, value });
      }
    });
  }
  return fields;
}

export function describeEventPayload(
  payload: Record<string, unknown> | null | undefined
): DetailField[] {
  if (!payload) return [];
  const fields: DetailField[] = [];

  const prompt = asObj(payload.userPrompt);
  if (prompt) {
    fields.push(...text('Prompt', prompt.text));
    const attachments = Array.isArray(prompt.attachments)
      ? prompt.attachments.filter(a => typeof a === 'string')
      : [];
    if (attachments.length > 0) {
      fields.push({
        label: 'Attachments',
        value: { kind: 'text', text: attachments.join('\n') },
      });
    }
  }

  const response = asObj(payload.aiResponse);
  if (response) {
    fields.push(...text('Response', response.text));
    fields.push(...text('Thinking', response.thinkingText));
  }

  const shell = asObj(payload.shellCommand);
  if (shell) {
    fields.push(...code('Command', shell.commandLine));
    fields.push(...text('Working directory', shell.cwd));
    fields.push(...code('Output', shell.output));
    fields.push(...duration(shell.durationMs));
  }

  const access = asObj(payload.fileAccess);
  if (access) {
    fields.push(...text('File', access.path));
    const op = asText(access.operation);
    if (op && op !== 'unspecified') fields.push(...text('Operation', op));
  }

  const edit = asObj(payload.fileEdit);
  if (edit) {
    if (edit.path !== access?.path) fields.push(...text('File', edit.path));
    const edits = Array.isArray(edit.edits) ? edit.edits : [];
    edits.forEach((raw, i) => {
      const e = asObj(raw);
      if (!e) return;
      const n = edits.length > 1 ? ` ${i + 1}` : '';
      if (e.isCompleteFile === true) {
        fields.push(...code(`New content${n}`, e.newString));
      } else {
        fields.push(...code(`Replaced${n}`, e.oldString));
        fields.push(...code(`With${n}`, e.newString));
      }
    });
  }

  const mcp = asObj(payload.mcpToolCall);
  if (mcp) {
    fields.push(...text('MCP server', mcp.serverName));
    fields.push(...text('Tool', mcp.toolName));
    const input = asText(mcp.toolInput);
    if (input) fields.push(...jsonTextFields(input, 'Arguments'));
    const result = asText(mcp.toolResult);
    if (result) fields.push(...code('Result', result));
    fields.push(...duration(mcp.durationMs));
  }

  // Payloads stored before the backend dropped repeated content still carry
  // copies in toolInput, toolResult and textToScan; only values not already
  // shown are added.
  const shown = new Set(fields.map(f => f.value.text));
  const editTexts = (Array.isArray(edit?.edits) ? edit.edits : [])
    .map(e => asObj(e)?.newString)
    .filter((s): s is string => typeof s === 'string');
  if (editTexts.length > 1) shown.add(editTexts.join('\n'));
  const pushNew = (more: DetailField[]) => {
    const added = more.filter(
      f => scalarValues.has(f.value) || !shown.has(f.value.text)
    );
    fields.push(...added);
    for (const f of added) {
      if (!scalarValues.has(f.value)) shown.add(f.value.text);
    }
  };

  const toolInput = asText(payload.toolInput);
  if (toolInput && toolInput !== asText(mcp?.toolInput)) {
    const parsed = asObj(parseJson(toolInput));
    // A tool input that is not a JSON object here was cut by the agent and
    // repeats the command, edit or MCP section the row already shows.
    if (parsed && edit) {
      pushNew(editInputFields(parsed, edit));
    } else if (parsed || !(shell || edit || mcp)) {
      pushNew(jsonTextFields(toolInput, 'Input'));
    }
  }
  pushNew(code('Result', payload.toolResult));
  pushNew(code('Scanned content', asObj(payload.textToScan)?.content));

  if (fields.length === 0) {
    const known = new Set([
      'userPrompt',
      'aiResponse',
      'shellCommand',
      'fileAccess',
      'fileEdit',
      'mcpToolCall',
      'textToScan',
      'toolInput',
      'toolResult',
    ]);
    const rest = Object.fromEntries(
      Object.entries(payload).filter(([k]) => !known.has(k))
    );
    for (const [key, v] of Object.entries(rest)) {
      const obj = asObj(v);
      if (obj) fields.push(...objectFields(obj, keyLabel('Payload', key)));
      else {
        const value = scalarOrCode(v);
        if (value) fields.push({ label: keyLabel('Payload', key), value });
      }
    }
  }
  return fields;
}

/**
 * The expanded row: the payload's fields, then the agent's state when the
 * event happened (model, permission mode, working directory), which can
 * change mid-session. A working directory the payload already shows is not
 * repeated.
 */
export function describeEventDetail(event: {
  payload: Record<string, unknown> | null;
  model: string | null;
  permissionMode: string | null;
  cwd: string | null;
}): DetailField[] {
  const fields = describeEventPayload(event.payload);
  const cwdShown = fields.some(
    f => f.label === 'Working directory' && f.value.text === event.cwd
  );
  return [
    ...fields,
    ...text('Model', event.model),
    ...text('Permission mode', event.permissionMode),
    ...(cwdShown ? [] : text('Working directory', event.cwd)),
  ];
}
